"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StructuredClient = void 0;
const anchor = __importStar(require("@project-serum/anchor"));
const web3_js_1 = require("@solana/web3.js");
const pda_1 = require("../pda");
const constants_1 = require("../utils/constants");
const spl_token_1 = require("@solana/spl-token");
const helpers_1 = require("../utils/helpers");
const axios_1 = __importDefault(require("axios"));
class StructuredClient {
    constructor(provider) {
        this.provider = provider;
        this.programs = {};
    }
    /**
     * Attach to a specific vault to begin querying the vault for user info
     */
    attach(identifier) {
        const programId = identifier.programId().toString();
        if (!(programId in this.programs)) {
            this.programs[programId] = identifier.createProgram(this.provider);
            this.programId = new web3_js_1.PublicKey(programId);
        }
        this.identityContext = identifier.asIdentityContext();
    }
    // Gets the pending deposit amount in terms of underlying
    async getPendingDepositAmount(user) {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const state = await this.getState();
        const [userVaultAccountAddress] = await (0, pda_1.findUserAccountAddress)(user, state.key, this.programId);
        try {
            const userVaultAccount = await this.getProgram().account.userVaultAccount.fetch(userVaultAccountAddress);
            const { round, amount } = userVaultAccount.depositData;
            if (round.eq(state.round)) {
                return amount;
            }
            else {
                return new anchor.BN(0);
            }
        }
        catch (err) {
            return new anchor.BN(0);
        }
    }
    // Get unredeemed user shares 
    async getUnredeemedShares(user) {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const state = await this.getState();
        // Check the user vault account
        // Two parts: 
        // 1) from the round and amount
        // 2) from the unredeemed shares in the deposit data  
        const [userVaultAccountAddress] = await (0, pda_1.findUserAccountAddress)(user, state.key, this.programId);
        try {
            const userVaultAccount = await this.getProgram().account.userVaultAccount.fetch(userVaultAccountAddress);
            const { round, amount, unredeemedShares } = userVaultAccount.depositData;
            let computedShares = new anchor.BN(0);
            if (amount.gtn(0) && !round.eq(state.round)) {
                const pageIndex = round.divn(constants_1.ROUNDS_PER_PAGE).toNumber();
                const [pricePerShareAddress] = await (0, pda_1.findPricePerShareAddress)(this.identityContext, pageIndex, this.programId);
                const pricePerSharePage = await this.getProgram().account.pricePerSharePage.fetch(pricePerShareAddress);
                const pricePerShare = pricePerSharePage.prices[round.toNumber()];
                const sharePrecision = new anchor.BN(10 ** state.decimals);
                computedShares = pricePerShare.gtn(0) ?
                    amount.mul(sharePrecision).div(pricePerShare)
                    : amount;
            }
            return computedShares.add(unredeemedShares);
        }
        catch (err) {
            return new anchor.BN(0);
        }
    }
    // Get total user shares owned by the user or claimable by the user
    async getTotalShares(user) {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const unredeemedShares = await this.getUnredeemedShares(user);
        const state = await this.getState();
        const tokenAccount = await spl_token_1.Token.getAssociatedTokenAddress(spl_token_1.ASSOCIATED_TOKEN_PROGRAM_ID, spl_token_1.TOKEN_PROGRAM_ID, state.derivativeTokenMint, user);
        const ownedShares = await (0, helpers_1.getBalanceFromProvider)(this.provider, tokenAccount);
        return unredeemedShares.add(ownedShares);
    }
    async getTotalSharesPendingWithdrawal(user) {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const state = await this.getState();
        const [userVaultAccountAddress] = await (0, pda_1.findUserAccountAddress)(user, state.key, this.programId);
        try {
            const userVaultAccount = await this.getProgram().account.userVaultAccount.fetch(userVaultAccountAddress);
            return userVaultAccount.withdrawData.shares;
        }
        catch (err) {
            return new anchor.BN(0);
        }
    }
    // Get total user share value in underlying 
    async getTotalShareValueInUnderlying(shareAmount) {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        // Fetch the current round
        const state = await this.getState();
        const pageIndex = state.round.divn(constants_1.ROUNDS_PER_PAGE).toNumber();
        const [pricePerShareAddress] = await (0, pda_1.findPricePerShareAddress)(this.identityContext, pageIndex, this.programId);
        const pricePerSharePage = await this.getProgram().account.pricePerSharePage.fetch(pricePerShareAddress);
        const currentSharePrice = pricePerSharePage.prices[state.round.toNumber() - 1];
        const sharePrecision = new anchor.BN(10 ** state.decimals);
        const totalShareValue = currentSharePrice.mul(shareAmount).div(sharePrecision);
        return totalShareValue;
    }
    // Get the total amount of underlying assets deposited into the current vault
    async getTotalBalance() {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const state = await this.getState();
        return state.lockedAmount.add(state.totalPendingDeposits);
    }
    async getPremium() {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const res = await axios_1.default.get("https://raw.githubusercontent.com/Katana-Labs/statistics/master/options/premiums.json");
        const data = res.data;
        const state = await this.getState();
        return new anchor.BN(data[state.key.toString()]);
    }
    // Get the latest option info
    async getOptionInfo() {
        if (!this.identityContext) {
            throw new Error("Identifier required");
        }
        const [optionParametersAddress] = await (0, pda_1.findOptionParameters)(this.identityContext, this.programId);
        return (await this.getProgram().account.optionParameters.fetch(optionParametersAddress));
    }
    getProgram() {
        if (!this.identityContext) {
            throw new Error("Must have identity context set");
        }
        return this.programs[this.programId.toString()];
    }
    async getState() {
        const [stateAddress] = await (0, pda_1.findStateAddress)(this.identityContext, this.programId);
        return {
            ...(await this.getProgram().account.state.fetch(stateAddress)),
            key: stateAddress
        };
    }
}
exports.StructuredClient = StructuredClient;
